import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import cn from 'classnames';
import { useTranslation } from 'react-i18next/';
import { faCheck, faTimes, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { GetStaticPropsContext } from 'next';

import Content from '@utils/Content';
import ErrorHelper from '@helpers/ErrorHelper';
import {
  DestinationLinksProvider,
  FooterLinksProvider,
  SiteLinksProvider,
  useAdvertisement,
  useLocale,
} from '@hooks/index';

import styles from './Category.module.scss';
import { useStores } from '@models/RootStore/RootStore';
import { TContent } from '@models/content_elements/Content/types';
import SidebarTravelTipsBlock from '@components/containers/SidebarTravelTipsBlock/SidebarTravelTipsBlock';
import TravelSuggestion from '@components/containers/TravelSuggestion/TravelSuggestion';
import { Meta } from '@components/layout/Meta/Meta';
import TipList from '@components/lists/TipList/TipList';
import LoginModal from '@components/modals/LoginModal/LoginModal';
import RegisterModal from '@components/modals/RegisterModal/RegisterModal';
import DescriptiveButton from '@components/ui/DescriptiveButton/DescriptiveButton';
import Layout from '@components/layout/Layout/Layout';
import { FB_APP_ID, REVALIDATION_TIME } from '@constants/config';
import GenericService from '@services/GenericService';
import GraphqlService from '@services/GraphqlService';
import MailchimpService from '@services/MailchimpService';
import PersonalService from '@services/PersonalService';
import { PageProps } from '@constants/types';
import { CategoryModule } from 'modules/CategoryModule/CategoryModule.service';
import { ICategoryPublicationFields } from 'modules/CategoryModule/CategoryModule.props';
import { ADVERTISEMENT_TYPE, ADVERTISEMENT_DEVICE } from '@constants/enums';
import { Advertisement } from '@components/layout/Advertisement/Advertisement';
import { BucketListTipModule } from 'modules/BucketListTipModule';
import PreprUtil from '@utils/PreprUtil';
import { LazyLoad } from '@components/containers';
import { CookieDialog } from '@components/forms';

const { FacebookProvider } = require('react-facebook');

export interface CategoryPageProps extends PageProps<ICategoryPublicationFields> {}

const CategoryPage = ({ fields, footerLinks, destinationLinks, siteLinks }: CategoryPageProps) => {
  const { t } = useTranslation();
  const { UserStore } = useStores();
  const locale = useLocale();
  const [bucketListIsLoading, setBucketListLoading] = useState(false);
  const [visitedIsLoading, setVisitedLoading] = useState(false);
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setRegisterModalOpen] = useState(false);
  const [isBucketList, setBucketList] = useState(false);
  const [hasVisited, setVisited] = useState(false);
  const [tips, setTips] = useState(fields.bucketListTips);
  const canLoadMore = tips.length < fields.totalTipsCount;

  useAdvertisement(
    ADVERTISEMENT_TYPE.ARTICLE,
    ADVERTISEMENT_DEVICE.DESKTOP_AND_MOBILE,
    `.${styles.Content} h2`,
    1,
  );

  useAdvertisement(
    ADVERTISEMENT_TYPE.ARTICLE,
    ADVERTISEMENT_DEVICE.DESKTOP_AND_MOBILE,
    `.${styles.Content} h2`,
    5,
  );

  useAdvertisement(
    ADVERTISEMENT_TYPE.ARTICLE,
    ADVERTISEMENT_DEVICE.DESKTOP_AND_MOBILE,
    `.${styles.Content} h2`,
    15,
  );

  useAdvertisement(
    ADVERTISEMENT_TYPE.ARTICLE,
    ADVERTISEMENT_DEVICE.DESKTOP_AND_MOBILE,
    `.${styles.Content} h2`,
    25,
  );

  const isDP = locale === 'nl-NL';

  const {
    _id,
    _bookmarks,
    _subscribes,
    _likes,
    _votes,
    title,
    subtitle,
    excerpt,
    sidebar,
    content,
    travel_suggestion_element,
    bucketListTips,
  } = fields;

  const bookmarks = isDP ? _bookmarks : _likes;
  const subscribes = isDP ? _subscribes : _votes;

  const bucketListButtonText = t('DOMAIN.BUCKETLIST.ON_BUCKETLISTS_COUNT', {
    count: bookmarks || 0,
  });
  const visitedButtonText = t('DOMAIN.BUCKETLIST.VISITED_COUNT', { count: subscribes || 0 });

  useEffect(() => {
    setBucketList(UserStore.isOnBucketList(_id));
    setVisited(UserStore.hasVisited(_id));
  }, [UserStore.hasLoaded, UserStore.bucketList.length, UserStore.visitedTips.length]);

  const fetchMoreTips = async (pageNr: number) => {
    if (!canLoadMore) {
      return;
    }

    const result = await BucketListTipModule.getTipsForTag(
      fields.category[0].body,
      (pageNr - 1) * 6,
      6,
    );

    const formattedResults = result.items?.map(PreprUtil.bucketListToProps) || [];

    setTips((tips) => {
      return [...tips, ...formattedResults];
    });
  };

  const handleToggleTipPress = async () => {
    setBucketListLoading(true);

    try {
      await UserStore.toggleBucketList(_id);
    } catch (e) {
      ErrorHelper.handleGenericError();
    } finally {
      setBucketListLoading(false);
    }
  };

  const handleAddToVisitedPress = async () => {
    setVisitedLoading(true);

    try {
      await UserStore.toggleVisited(_id);
    } catch (e) {
      ErrorHelper.handleGenericError();
    } finally {
      setVisitedLoading(false);
    }
  };

  const renderContent = (items: TContent) => {
    return items.map((item) => {
      return Content.renderPreprElement(item);
    });
  };

  const handleRegisterClick = () => {
    setLoginModalOpen(false);
    setRegisterModalOpen(true);
  };

  return (
    <FacebookProvider appId={FB_APP_ID} xfbml>
      <FooterLinksProvider value={footerLinks}>
        <DestinationLinksProvider value={destinationLinks}>
          <SiteLinksProvider value={siteLinks}>
            <Layout paddings={false}>
              <div className={cn(styles.CategoryPage, 'container')}>
                <Meta
                  title={fields.meta_titel || fields.meta_title || fields.title}
                  description={fields.meta_description || fields.subtitle}
                  image={fields.thumbnail?.[0]?.cdn_files[0]?.full}
                />
                <LoginModal
                  isOpen={isLoginModalOpen}
                  onRegisterClick={handleRegisterClick}
                  onClose={() => setLoginModalOpen(false)}
                />
                <RegisterModal
                  isOpen={isRegisterModalOpen}
                  onClose={() => setRegisterModalOpen(false)}
                />
                <div className="container">
                  <div className="row">
                    <div className="col-lg-8">
                      <h1 className="h1--large">{title}</h1>
                      <h2>{subtitle}</h2>
                      <div className={styles.Buttons}>
                        <DescriptiveButton
                          icon={isBucketList ? faTimes : faPlus}
                          iconPosition="left"
                          label={t('DOMAIN.BUCKETLIST.BUCKETLIST')}
                          className={styles.Button}
                          innerClassName={styles.ButtonInner}
                          buttonClassName={styles.InnerButton}
                          description={bucketListButtonText || ''}
                          onPress={
                            UserStore.isAuthenticated
                              ? handleToggleTipPress
                              : () => setLoginModalOpen(true)
                          }
                          isLoading={bucketListIsLoading}
                        />
                        <DescriptiveButton
                          icon={hasVisited ? faTimes : faCheck}
                          iconPosition="left"
                          label={t('DOMAIN.BUCKETLIST.VISITED')}
                          className={styles.Button}
                          innerClassName={styles.ButtonInner}
                          buttonClassName={styles.InnerButton}
                          description={visitedButtonText || ''}
                          onPress={
                            UserStore.isAuthenticated
                              ? handleAddToVisitedPress
                              : () => setLoginModalOpen(true)
                          }
                          isLoading={visitedIsLoading}
                        />
                      </div>
                      {!!excerpt && (
                        <div className={cn(styles.Content, 'mt-xl')}>{renderContent(excerpt)}</div>
                      )}
                      {!!travel_suggestion_element && !!travel_suggestion_element[0] && (
                        <div className="mt-xl">
                          <TravelSuggestion
                            title={travel_suggestion_element[0].display_title}
                            backgroundImage={
                              travel_suggestion_element[0].background_image[0].cdn_files[0]?.full
                            }
                            description={travel_suggestion_element[0].description}
                            link={{
                              url: travel_suggestion_element[0].button_url,
                              label: travel_suggestion_element[0].button_label,
                            }}
                          />
                        </div>
                      )}
                      {!!content && (
                        <div className={cn(styles.Content, 'content')}>
                          {renderContent(content)}
                        </div>
                      )}
                    </div>
                    {!!sidebar && !!sidebar[0] && (
                      <div className={cn(styles.SideBarDesktop, 'col-lg-4')}>
                        <SidebarTravelTipsBlock
                          title={sidebar[0].display_title}
                          image={sidebar[0].photo?.[0].cdn_files?.[0].full}
                          description={sidebar[0].description}
                          topColumn={sidebar[0].first_column}
                          bottomColumn={sidebar[0].second_column}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Advertisement advertisementType={ADVERTISEMENT_TYPE.ARTICLE} />
                </div>
                {!!bucketListTips && bucketListTips.length > 0 && (
                  <section className="mt-xxl">
                    <h2 className={styles.TipsHeader}>
                      {t('DOMAIN.BUCKETLIST.ALL_ABOUT_ARTICLE', {
                        count: fields.totalTipsCount,
                        title,
                      })}
                    </h2>
                    <LazyLoad callback={fetchMoreTips} canLoadMore={canLoadMore}>
                      <TipList shouldSort={false} largeTips tips={tips} />
                    </LazyLoad>
                  </section>
                )}
                {!!sidebar && !!sidebar[0] && (
                  <div className={cn(styles.SideBarMobile, 'col')}>
                    <SidebarTravelTipsBlock
                      title={sidebar[0].display_title}
                      image={sidebar[0].photo?.[0].cdn_files?.[0].full}
                      description={sidebar[0].description}
                      topColumn={sidebar[0].first_column}
                      bottomColumn={sidebar[0].second_column}
                    />
                  </div>
                )}
              </div>
            </Layout>
            <CookieDialog />
          </SiteLinksProvider>
        </DestinationLinksProvider>
      </FooterLinksProvider>
    </FacebookProvider>
  );
};

export default observer(CategoryPage);

export const getStaticProps = async ({ locale, preview, params }: GetStaticPropsContext) => {
  if (!params) {
    return {
      notFound: true,
    };
  }

  GraphqlService.setLocale(locale);
  PersonalService.setLocale(locale);
  GenericService.setLocale(locale);
  MailchimpService.setLocale(locale);

  GraphqlService.setPreview(!!preview);

  const page = await CategoryModule.getSinglePage(String(params.category));

  if (!page || !page.fields) {
    return {
      notFound: true,
      revalidate: REVALIDATION_TIME,
    };
  }

  return {
    props: page,
    revalidate: REVALIDATION_TIME,
  };
};

export const getStaticPaths = async () => {
  const paths = {
    paths: [],
    fallback: 'blocking',
  };

  return paths;
};
